import React, { useMemo } from 'react';
import { useWorkshopStore, type WorkshopSubView } from '../store/workshopStore';
import { PanelView } from './panel/PanelView';
import { SchematicView } from './schematic/SchematicView';
import { WorkshopToolbox } from './toolbox/WorkshopToolbox';
import { ElectricalSolver } from '../solver/ElectricalSolver';
import { PRESET_CIRCUITS } from '../model/defaultCircuits';
import type { WireColor } from '../model/types';

const WIRE_COLORS: { label: string; color: WireColor }[] = [
  { label: 'Siyah (Kumanda)', color: '#1f2937' },
  { label: 'Kahverengi (L1)', color: '#78350f' },
  { label: 'Gri (L3)', color: '#64748b' },
  { label: 'Mavi (Nötr)', color: '#0284c7' },
  { label: 'Sarı-Yeşil (PE)', color: '#84cc16' },
];

export const WorkshopView: React.FC = () => {
  const activeSubView = useWorkshopStore((s) => s.activeSubView);
  const setSubView = useWorkshopStore((s) => s.setSubView);
  const project = useWorkshopStore((s) => s.project);
  const setProject = useWorkshopStore((s) => s.setProject);
  const updateDeviceProp = useWorkshopStore((s) => s.updateDeviceProp);
  const resetCircuit = useWorkshopStore((s) => s.resetCircuit);
  const loadPreset = useWorkshopStore((s) => s.loadPreset);
  const activeWireColor = useWorkshopStore((s) => s.activeWireColor);
  const setActiveWireColor = useWorkshopStore((s) => s.setActiveWireColor);
  const isSimulating = useWorkshopStore((s) => s.isSimulating);
  const setSimulating = useWorkshopStore((s) => s.setSimulating);
  const isMuted = useWorkshopStore((s) => s.isMuted);
  const setMuted = useWorkshopStore((s) => s.setMuted);

  const currentPresetId = project.id.includes('empty')
    ? 'empty'
    : project.id.includes('jogging')
    ? 'jogging'
    : project.id.includes('troubleshooting')
    ? 'troubleshooting'
    : 'dol';

  const nodePotentials = useMemo(() => {
    return new ElectricalSolver().solve(project).nodePotentials;
  }, [project]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        background: '#090d16',
        overflow: 'hidden',
      }}
    >
      {/* Üst Atölye Araç Çubuğu */}
      <header
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '10px 20px',
          background: '#141c2b',
          borderBottom: '1px solid #243044',
          zIndex: 10,
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        {/* Sol Küme: Görünüm Modu ve Şablon Seçimi */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {/* Görünüm Modu Seçimi (Pano / Şema / Yan Yana) */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#0b0f17', padding: '4px', borderRadius: 8 }}>
            {(
              [
                { id: 'panel', label: '📦 Fiziksel Pano' },
                { id: 'schematic', label: '📄 Elektrik Şeması' },
                { id: 'split', label: '🔀 Yan Yana (Split)' },
              ] as const
            ).map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setSubView(tab.id as WorkshopSubView)}
                style={{
                  background: activeSubView === tab.id ? '#0284c7' : 'transparent',
                  color: activeSubView === tab.id ? '#ffffff' : '#94a3b8',
                  border: 'none',
                  padding: '6px 14px',
                  borderRadius: 6,
                  fontWeight: 600,
                  fontSize: 12.5,
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Eğitim Devre Şablonları Seçici */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#1c2637', padding: '5px 10px', borderRadius: 8, border: '1px solid #2d3b52' }}>
            <span style={{ fontSize: 12, color: '#94a3b8' }}>Şablon:</span>
            <select
              value={currentPresetId}
              onChange={(e) => loadPreset(e.target.value)}
              style={{
                background: '#0f172a',
                color: '#f8fafc',
                border: '1px solid #475569',
                borderRadius: 4,
                padding: '4px 8px',
                fontSize: 12,
                cursor: 'pointer',
              }}
            >
              {PRESET_CIRCUITS.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Kablo Rengi Seçici */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#1c2637', padding: '6px 12px', borderRadius: 8, border: '1px solid #2d3b52' }}>
          <span style={{ fontSize: 12, color: '#94a3b8' }}>Kablo Rengi:</span>
          {WIRE_COLORS.map((w) => (
            <button
              key={w.color}
              type="button"
              title={w.label}
              onClick={() => setActiveWireColor(w.color)}
              style={{
                width: 18,
                height: 18,
                borderRadius: '50%',
                background: w.color,
                border: activeWireColor === w.color ? '2.5px solid #38bdf8' : '1px solid #475569',
                cursor: 'pointer',
                transform: activeWireColor === w.color ? 'scale(1.2)' : 'none',
                transition: 'transform 0.1s',
              }}
            />
          ))}
          <span style={{ fontSize: 11, color: '#64748b', marginLeft: 6 }}>
            {project.wires.length} Kablo
          </span>
        </div>

        {/* Eylem Butonları */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button
            type="button"
            onClick={() => setMuted(!isMuted)}
            title={isMuted ? 'Mekanik ses efektlerini aç' : 'Mekanik ses efektlerini kapat'}
            style={{
              background: isMuted ? '#334155' : '#0369a1',
              color: '#ffffff',
              border: 'none',
              padding: '6px 10px',
              borderRadius: 6,
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {isMuted ? '🔇 Ses: Kapalı' : '🔊 Ses: Açık'}
          </button>

          <button
            type="button"
            onClick={() => setSimulating(!isSimulating)}
            style={{
              background: isSimulating ? '#166534' : '#334155',
              color: '#ffffff',
              border: 'none',
              padding: '6px 12px',
              borderRadius: 6,
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {isSimulating ? '⚡ Simülasyon: AÇIK' : '⏸ Simülasyon: DURDU'}
          </button>

          <button
            type="button"
            onClick={() => {
              if (window.confirm('Devreyi sıfırlamak istiyor musunuz?')) {
                resetCircuit();
              }
            }}
            style={{
              background: '#334155',
              color: '#cbd5e1',
              border: 'none',
              padding: '6px 12px',
              borderRadius: 6,
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            Devreyi Sıfırla
          </button>
        </div>
      </header>

      {/* Ana Çizim Alanı */}
      <main style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Sol Eleman Kütüphanesi ve Canlı Multimetre */}
        <WorkshopToolbox project={project} nodePotentials={nodePotentials} />

        {activeSubView === 'panel' && (
          <div style={{ flex: 1, height: '100%', overflow: 'auto' }}>
            <PanelView
              project={project}
              onChangeProject={setProject}
              activeWireColor={activeWireColor}
              isSimulating={isSimulating}
            />
          </div>
        )}

        {activeSubView === 'schematic' && (
          <div style={{ flex: 1, height: '100%', overflow: 'auto' }}>
            <SchematicView
              project={project}
              onChangeProject={setProject}
              onUpdateDeviceProp={updateDeviceProp}
              activeWireColor={activeWireColor}
              isSimulating={isSimulating}
            />
          </div>
        )}

        {activeSubView === 'split' && (
          <div style={{ display: 'flex', flex: 1, height: '100%', overflow: 'hidden' }}>
            <div style={{ flex: 1, height: '100%', overflow: 'auto', borderRight: '1px solid #243044' }}>
              <PanelView
                project={project}
                onChangeProject={setProject}
                activeWireColor={activeWireColor}
                isSimulating={isSimulating}
              />
            </div>
            <div style={{ flex: 1, height: '100%', overflow: 'auto' }}>
              <SchematicView
                project={project}
                onChangeProject={setProject}
                onUpdateDeviceProp={updateDeviceProp}
                activeWireColor={activeWireColor}
                isSimulating={isSimulating}
              />
            </div>
          </div>
        )}
      </main>
    </div>
  );
};
